// src/components/AdherentEditModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Badge,
  Card,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { getFonctions, getGares, getDistricts } from "../api/api";
import { useNotifications } from "./NotificationsContext";

export default function AdherentEditModal({ show, onHide, adherent, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [fonctions, setFonctions] = useState([]);
  const [gares, setGares] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [errors, setErrors] = useState({});
  const { addNotification } = useNotifications();

  // Parents (affichage seulement)
  const [parentsAlive, setParentsAlive] = useState({
    father: { alive: true, deceased: false },
    mother: { alive: true, deceased: false },
  });

  // Password settings
  const [passwordSettings, setPasswordSettings] = useState({
    resetPassword: false,
    showPassword: false,
    showOldPassword: false,
    generatedPassword: "",
    passwordField: "",
    passwordCopied: false,
  });

  // Form data
  const [data, setData] = useState({
    first_name: "",
    last_name: "",
    matricule: "",
    cin: "",
    telephone: "",
    situation_familiale: "celibataire",
    fonction: "",
    gare: "",
    district: "",
    is_amical_admin: false,
    email: "",
  });

  // Helpers: allow adherent.fonction to be {id:...} OR number/string id
  const toIdString = (maybeObjOrId) => {
    const id = maybeObjOrId?.id ?? maybeObjOrId ?? "";
    return id === null || id === undefined ? "" : String(id);
  };

  // -------------------------
  // Password
  // -------------------------
  const generatePassword = () => {
    const newPass = Math.random().toString(36).slice(-10);
    setPasswordSettings((prev) => ({
      ...prev,
      generatedPassword: newPass,
      passwordField: newPass,
      showPassword: true,
      passwordCopied: false,
    }));
  };

  const handleCopyPassword = () => {
    if (!passwordSettings.passwordField) return;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(passwordSettings.passwordField)
        .then(() => {
          setPasswordSettings((prev) => ({ ...prev, passwordCopied: true }));
        })
        .catch(() => {
          addNotification({
            type: "error",
            title: "Erreur",
            message: "Impossible de copier le mot de passe.",
            delay: 4000,
          });
        });
    }
  };

  // -------------------------
  // Digits-only handlers (8)
  // -------------------------
  const clean8Digits = (raw) => String(raw || "").replace(/\D/g, "").slice(0, 8);

  const handleMatriculeChange = (e) => {
    const value = clean8Digits(e.target.value);
    setData((prev) => ({ ...prev, matricule: value }));
    if (errors.matricule) setErrors((prev) => ({ ...prev, matricule: undefined }));
  };

  const handleTelephoneChange = (e) => {
    const value = clean8Digits(e.target.value);
    setData((prev) => ({ ...prev, telephone: value }));
    if (errors.telephone) setErrors((prev) => ({ ...prev, telephone: undefined }));
  };

  const handleCinChange = (e) => {
    const value = clean8Digits(e.target.value);
    setData((prev) => ({ ...prev, cin: value }));
    if (errors.cin) setErrors((prev) => ({ ...prev, cin: undefined }));
  };

  // Paste-safe: replace pasted content with sanitized digits
  const handleDigitsPaste = (e, field) => {
    const pasted = (e.clipboardData || window.clipboardData).getData("text");
    const value = clean8Digits(pasted);
    e.preventDefault();
    setData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // -------------------------
  // Load lists + hydrate form
  // -------------------------
  useEffect(() => {
    if (!show || !adherent) return;

    let cancelled = false;

    const fetchLists = async () => {
      try {
        setLoading(true);
        const [foncRes, gareRes, distRes] = await Promise.all([
          getFonctions(),
          getGares(),
          getDistricts(),
        ]);
        if (cancelled) return;

        setFonctions(foncRes.data || []);
        setGares(gareRes.data || []);
        setDistricts(distRes.data || []);
      } catch (err) {
        if (cancelled) return;
        addNotification({
          type: "error",
          title: "Erreur",
          message: "Impossible de charger les listes.",
          delay: 6000,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Hydrate form immediately (prefill)
    const initialData = {
      first_name: adherent.first_name || "",
      last_name: adherent.last_name || "",
      matricule: clean8Digits(adherent.matricule || ""),
      cin: clean8Digits(adherent.cin || ""),
      telephone: clean8Digits(adherent.telephone || ""),
      situation_familiale: adherent.situation_familiale || "celibataire",
      // ✅ Prefill selects robustly + as strings (important)
      fonction: toIdString(adherent.fonction),
      gare: toIdString(adherent.gare),
      district: toIdString(adherent.district),
      is_amical_admin: Boolean(adherent.is_amical_admin),
      email: adherent.email || "",
    };

    setData(initialData);

    // Parents status (display)
    const father = adherent.parents?.find((p) => p.type_parent === "pere");
    const mother = adherent.parents?.find((p) => p.type_parent === "mere");
    setParentsAlive({
      father: {
        alive: father ? father.situation !== "decede" : true,
        deceased: father ? father.situation === "decede" : false,
      },
      mother: {
        alive: mother ? mother.situation !== "decede" : true,
        deceased: mother ? mother.situation === "decede" : false,
      },
    });

    // Reset password settings
    setPasswordSettings({
      resetPassword: false,
      showPassword: false,
      showOldPassword: false,
      generatedPassword: "",
      passwordField: "",
      passwordCopied: false,
    });

    setErrors({});
    fetchLists();

    return () => {
      cancelled = true;
    };
  }, [show, adherent, addNotification]);

  // If options load later, keep the prefilled values (string vs number mismatch can blank the UI)
  // Ensure option values are also strings (done in JSX below). [web:22][web:25]

  // -------------------------
  // Validation
  // -------------------------
  const validate = () => {
    const newErrors = {};

    if (!data.first_name.trim()) newErrors.first_name = "Prénom requis";
    if (!data.last_name.trim()) newErrors.last_name = "Nom requis";

    // Strict 8 digits
    if (!data.matricule || data.matricule.length !== 8) {
      newErrors.matricule = "Matricule : exactement 8 chiffres requis";
    }
    if (!data.cin || data.cin.length !== 8) {
      newErrors.cin = "CIN : exactement 8 chiffres requis";
    }
    if (!data.telephone || data.telephone.length !== 8) {
      newErrors.telephone = "Téléphone : exactement 8 chiffres requis";
    }

    if (!data.fonction) newErrors.fonction = "Fonction requise";
    if (!data.gare) newErrors.gare = "Gare requise";
    if (!data.district) newErrors.district = "District requis";

    if (data.email && !/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = "Email invalide";
    }

    if (passwordSettings.resetPassword) {
      if (!passwordSettings.passwordCopied) {
        newErrors.password = "Veuillez copier et confirmer le nouveau mot de passe";
      } else if ((passwordSettings.passwordField || "").length < 8) {
        newErrors.password = "Le mot de passe doit contenir au moins 8 caractères";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -------------------------
  // Submit
  // -------------------------
  const handleSubmit = async () => {
    if (!adherent || !validate()) return;

    try {
      const updatedData = {};

      // Strings
      const safeEmail = data.email || "";
      if (safeEmail !== (adherent.email || "")) updatedData.email = safeEmail;

      if (data.first_name !== (adherent.first_name || "")) updatedData.first_name = data.first_name.trim();
      if (data.last_name !== (adherent.last_name || "")) updatedData.last_name = data.last_name.trim();

      // Digits fields
      const currentMatricule = clean8Digits(adherent.matricule || "");
      const currentCin = clean8Digits(adherent.cin || "");
      const currentTelephone = clean8Digits(adherent.telephone || "");
      if (data.matricule !== currentMatricule) updatedData.matricule = data.matricule;
      if (data.cin !== currentCin) updatedData.cin = data.cin;
      if (data.telephone !== currentTelephone) updatedData.telephone = data.telephone;

      if (data.situation_familiale !== (adherent.situation_familiale || "")) {
        updatedData.situation_familiale = data.situation_familiale;
      }

      // FKs must go as integers to backend
      const currentFonction = toIdString(adherent.fonction);
      const currentGare = toIdString(adherent.gare);
      const currentDistrict = toIdString(adherent.district);

      if (data.fonction && data.fonction !== currentFonction) updatedData.fonction = parseInt(data.fonction, 10);
      if (data.gare && data.gare !== currentGare) updatedData.gare = parseInt(data.gare, 10);
      if (data.district && data.district !== currentDistrict) updatedData.district = parseInt(data.district, 10);

      if (Boolean(data.is_amical_admin) !== Boolean(adherent.is_amical_admin)) {
        updatedData.is_amical_admin = Boolean(data.is_amical_admin);
      }

      if (passwordSettings.resetPassword && passwordSettings.passwordField) {
        updatedData.password = passwordSettings.passwordField;
      }

      if (Object.keys(updatedData).length === 0) {
        onHide();
        return;
      }

      await onUpdate(adherent.id, updatedData);

      onHide();
      addNotification({ type: "success", title: "Succès", message: "Mis à jour!", delay: 4000 });
    } catch (err) {
      addNotification({
        type: "error",
        title: "Erreur",
        message:
          err?.response?.data?.matricule?.[0] ||
          err?.response?.data?.cin?.[0] ||
          err?.response?.data?.telephone?.[0] ||
          err?.response?.data?.email?.[0] ||
          "Erreur mise à jour",
        delay: 7000,
      });
      throw err;
    }
  };

  // Counters (read-only)
  const enfantsCount = adherent?.enfants?.length || 0;
  const parentsAliveCount = useMemo(() => {
    const f = parentsAlive.father.alive ? 1 : 0;
    const m = parentsAlive.mother.alive ? 1 : 0;
    return f + m;
  }, [parentsAlive]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Modifier l&apos;adhérent {adherent ? <>– <small>#{adherent.id}</small></> : null}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {!adherent ? (
          <p>Aucun adhérent sélectionné.</p>
        ) : loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {/* Password */}
            <Card className="mb-3 border-warning">
              <Card.Header className="bg-warning py-2 d-flex justify-content-between align-items-center">
                <strong>Gestion du mot de passe</strong>
                <Form.Check
                  type="switch"
                  id="reset-password-switch"
                  label="Réinitialiser"
                  checked={passwordSettings.resetPassword}
                  onChange={() =>
                    setPasswordSettings((prev) => ({
                      ...prev,
                      resetPassword: !prev.resetPassword,
                      passwordField: "",
                      generatedPassword: "",
                      passwordCopied: false,
                    }))
                  }
                />
              </Card.Header>

              {passwordSettings.resetPassword && (
                <Card.Body>
                  <Alert variant="info" className="py-2">
                    <small>
                      <strong>Note :</strong> L&apos;adhérent utilisera ce nouveau mot de passe.
                    </small>
                  </Alert>

                  <Row className="mb-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Nouveau mot de passe *</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={passwordSettings.showPassword ? "text" : "password"}
                            readOnly
                            value={passwordSettings.passwordField}
                            isInvalid={!!errors.password}
                          />
                          <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={() =>
                              setPasswordSettings((prev) => ({
                                ...prev,
                                showPassword: !prev.showPassword,
                              }))
                            }
                          >
                            {passwordSettings.showPassword ? "🙈" : "👁️"}
                          </Button>
                          <Button type="button" variant="outline-primary" onClick={handleCopyPassword}>
                            Copier
                          </Button>
                          <Button type="button" variant="outline-danger" onClick={generatePassword}>
                            Générer
                          </Button>
                        </InputGroup>
                        <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Confirmation</Form.Label>
                        <Form.Check
                          checked={passwordSettings.passwordCopied}
                          onChange={(e) =>
                            setPasswordSettings((prev) => ({
                              ...prev,
                              passwordCopied: e.target.checked,
                            }))
                          }
                          label="✅ J'ai copié le mot de passe"
                          className="mt-2"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              )}
            </Card>

            {/* Summary */}
            <div className="mb-3 p-2 border rounded bg-light">
              <Row>
                <Col md={4}>
                  <div>
                    <strong>
                      {data.first_name} {data.last_name}
                    </strong>
                  </div>
                  <div>
                    Matricule : <b>{data.matricule}</b>
                  </div>
                  <div>
                    CIN : <b>{data.cin}</b>
                  </div>
                  <div>
                    Téléphone : <b>{data.telephone}</b>
                  </div>
                </Col>
                <Col md={4}>
                  <div>
                    Situation : <b>{data.situation_familiale}</b>
                  </div>
                  <div>
                    Parents vivants :{" "}
                    <Badge bg={parentsAliveCount === 2 ? "success" : parentsAliveCount === 1 ? "warning" : "danger"}>
                      {parentsAliveCount}/2
                    </Badge>
                  </div>
                  <div>
                    Enfants : <Badge bg="secondary">{enfantsCount}</Badge>
                  </div>
                </Col>
                <Col md={4}>
                  <div>
                    Admin amicale :{" "}
                    {data.is_amical_admin ? <Badge bg="success">Oui</Badge> : <Badge bg="secondary">Non</Badge>}
                  </div>
                </Col>
              </Row>
            </div>

            <Form>
              {/* Matricule + Telephone (8 digits) */}
              <Row className="mb-3">
                <Col>
                  <Form.Label>Matricule * (8 chiffres)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    value={data.matricule}
                    onChange={handleMatriculeChange}
                    onPaste={(e) => handleDigitsPaste(e, "matricule")}
                    maxLength={8}
                    isInvalid={!!errors.matricule}
                  />
                  <Form.Control.Feedback type="invalid">{errors.matricule}</Form.Control.Feedback>
                </Col>

                <Col>
                  <Form.Label>Téléphone * (8 chiffres)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    value={data.telephone}
                    onChange={handleTelephoneChange}
                    onPaste={(e) => handleDigitsPaste(e, "telephone")}
                    maxLength={8}
                    isInvalid={!!errors.telephone}
                  />
                  <Form.Control.Feedback type="invalid">{errors.telephone}</Form.Control.Feedback>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Label>CIN * (8 chiffres)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    value={data.cin}
                    onChange={handleCinChange}
                    onPaste={(e) => handleDigitsPaste(e, "cin")}
                    maxLength={8}
                    isInvalid={!!errors.cin}
                  />
                  <Form.Control.Feedback type="invalid">{errors.cin}</Form.Control.Feedback>
                </Col>
              </Row>

              {/* Name */}
              <Row className="mb-3">
                <Col>
                  <Form.Label>Prénom *</Form.Label>
                  <Form.Control
                    value={data.first_name}
                    onChange={(e) => setData((prev) => ({ ...prev, first_name: e.target.value }))}
                    isInvalid={!!errors.first_name}
                  />
                  <Form.Control.Feedback type="invalid">{errors.first_name}</Form.Control.Feedback>
                </Col>

                <Col>
                  <Form.Label>Nom *</Form.Label>
                  <Form.Control
                    value={data.last_name}
                    onChange={(e) => setData((prev) => ({ ...prev, last_name: e.target.value }))}
                    isInvalid={!!errors.last_name}
                  />
                  <Form.Control.Feedback type="invalid">{errors.last_name}</Form.Control.Feedback>
                </Col>
              </Row>

              {/* Email + Situation */}
              <Row className="mb-3">
                <Col>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={data.email}
                    onChange={(e) => setData((prev) => ({ ...prev, email: e.target.value }))}
                    isInvalid={!!errors.email}
                    placeholder="exemple@domaine.com"
                  />
                  <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Col>

                <Col>
                  <Form.Label>Situation familiale *</Form.Label>
                  <Form.Select
                    value={data.situation_familiale}
                    onChange={(e) => setData((prev) => ({ ...prev, situation_familiale: e.target.value }))}
                  >
                    <option value="celibataire">Célibataire</option>
                    <option value="marie">Marié(e)</option>
                    <option value="divorce">Divorcé(e)</option>
                    <option value="veuf">Veuf/Veuve</option>
                  </Form.Select>
                </Col>
              </Row>

              {/* Prefilled selects */}
              <Row className="mb-3">
                <Col>
                  <Form.Label>Fonction *</Form.Label>
                  <Form.Select
                    value={data.fonction}
                    onChange={(e) => setData((prev) => ({ ...prev, fonction: e.target.value }))}
                    isInvalid={!!errors.fonction}
                  >
                    <option value="">-- Sélectionner --</option>
                    {fonctions.map((f) => (
                      <option key={f.id} value={String(f.id)}>
                        {f.nom}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errors.fonction}</Form.Control.Feedback>
                </Col>

                <Col>
                  <Form.Label>Gare *</Form.Label>
                  <Form.Select
                    value={data.gare}
                    onChange={(e) => setData((prev) => ({ ...prev, gare: e.target.value }))}
                    isInvalid={!!errors.gare}
                  >
                    <option value="">-- Sélectionner --</option>
                    {gares.map((g) => (
                      <option key={g.id} value={String(g.id)}>
                        {g.nom}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errors.gare}</Form.Control.Feedback>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Label>District *</Form.Label>
                  <Form.Select
                    value={data.district}
                    onChange={(e) => setData((prev) => ({ ...prev, district: e.target.value }))}
                    isInvalid={!!errors.district}
                  >
                    <option value="">-- Sélectionner --</option>
                    {districts.map((d) => (
                      <option key={d.id} value={String(d.id)}>
                        {d.nom}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errors.district}</Form.Control.Feedback>
                </Col>

                <Col>
                  <Form.Label>Admin Amicale</Form.Label>
                  <div className="mt-2">
                    <Form.Check
                      type="switch"
                      id="is_amical_admin"
                      label={data.is_amical_admin ? "Administrateur Actif" : "Membre Standard"}
                      checked={data.is_amical_admin}
                      onChange={(e) =>
                        setData((prev) => ({
                          ...prev,
                          is_amical_admin: e.target.checked,
                        }))
                      }
                    />
                  </div>
                </Col>
              </Row>

              <hr />

              {/* Parents read-only */}
              <h5>
                Parents ({parentsAliveCount}/2 vivants) <small className="text-muted">(Lecture seule)</small>
              </h5>
              {adherent.parents?.length === 0 ? (
                <p className="text-muted">Aucun parent enregistré.</p>
              ) : (
                adherent.parents.map((parent, index) => {
                  const isFather = parent.type_parent === "pere";
                  const parentState = isFather ? parentsAlive.father : parentsAlive.mother;

                  return (
                    <div
                      key={index}
                      className="border rounded p-2 mb-2"
                      style={{
                        background: parentState.alive ? "#f8f9fa" : "#f8d7da",
                        opacity: parentState.alive ? 1 : 0.8,
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong className="text-capitalize">{isFather ? "Père" : "Mère"}</strong>
                        <Badge bg={parentState.alive ? "success" : "danger"}>
                          {parentState.alive ? "Vivant" : "Décédé"}
                        </Badge>
                      </div>
                      <Row className="mt-2">
                        <Col>
                          <Form.Group>
                            <Form.Label>Nom</Form.Label>
                            <Form.Control value={parent.nom || ""} disabled />
                          </Form.Group>
                        </Col>
                        <Col>
                          <Form.Group>
                            <Form.Label>Prénom</Form.Label>
                            <Form.Control value={parent.prenom || ""} disabled />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col>
                          <Form.Group>
                            <Form.Label>Date de naissance</Form.Label>
                            <Form.Control type="date" value={parent.date_naissance || ""} disabled />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  );
                })
              )}

              <hr />

              {/* Enfants read-only */}
              <h5>
                Enfants ({enfantsCount}) <small className="text-muted">(Lecture seule)</small>
              </h5>
              {enfantsCount === 0 ? (
                <p className="text-muted">Aucun enfant enregistré.</p>
              ) : (
                adherent.enfants.map((enf, index) => (
                  <div key={index} className="border rounded p-2 mb-2" style={{ background: "#f8f9fa" }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{enf.prenom || "Sans nom"}</strong>
                      <Badge bg={enf.sexe === "M" ? "primary" : "danger"}>
                        {enf.sexe === "M" ? "Garçon" : "Fille"}
                      </Badge>
                    </div>
                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>Nom</Form.Label>
                          <Form.Control value={enf.nom || ""} disabled />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group>
                          <Form.Label>Prénom</Form.Label>
                          <Form.Control value={enf.prenom || ""} disabled />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <Form.Group>
                          <Form.Label>Date de naissance</Form.Label>
                          <Form.Control type="date" value={enf.date_naissance || ""} disabled />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group>
                          <Form.Label>Sexe</Form.Label>
                          <Form.Control value={enf.sexe === "M" ? "Masculin" : "Féminin"} disabled />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                ))
              )}
            </Form>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div>
            {passwordSettings.resetPassword && (
              <Alert variant="warning" className="mb-0 py-1 small">
                ⚠️ Le mot de passe sera réinitialisé
              </Alert>
            )}
          </div>

          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Fermer
            </Button>

            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading || (passwordSettings.resetPassword && !passwordSettings.passwordCopied)}
            >
              {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
              Enregistrer les modifications
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
